import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { 
  Gift, 
  Receipt, 
  Ticket, 
  TrendingUp,
  TrendingDown,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Sparkles,
  Percent,
  Check,
  Eye
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

// Storage key for rotation state
const ROTATION_STORAGE_KEY = "stelx_smart_card_rotation";
const CURRENCY_WATCH_KEY = "stelx_watched_currency";
const ROTATION_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes cooldown

const SmartCards = () => {
  const navigate = useNavigate();
  const [rotationIndex, setRotationIndex] = useState(0);
  const [lastRotationTime, setLastRotationTime] = useState(0);
  const [showCurrencyWatcher, setShowCurrencyWatcher] = useState(false);
  const [watchedCurrency, setWatchedCurrency] = useState(() => {
    return localStorage.getItem(CURRENCY_WATCH_KEY) || "USD";
  });

  // Fetch bills due soon (within 5 days)
  const { data: billsData } = useQuery({
    queryKey: ["billsDueSoon"],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API}/bills/due-soon?days=5`);
        return response.data;
      } catch {
        return { bills: [] };
      }
    },
    staleTime: 60000,
    retry: false,
  });

  // Fetch active promos/cashback offers
  const { data: promoData } = useQuery({
    queryKey: ["activePromos"],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API}/promos/active`);
        return response.data;
      } catch {
        return { activePromo: null };
      }
    },
    staleTime: 300000,
    retry: false,
  });

  // Fetch FX rates with change data
  const { data: fxData } = useQuery({
    queryKey: ["fxRatesWithChanges"],
    queryFn: async () => {
      try {
        const [ratesRes, changesRes] = await Promise.all([
          axios.get(`${API}/fx/rates`),
          axios.get(`${API}/fx/rates/changes`)
        ]);
        
        const rates = ratesRes.data;
        const changes = changesRes.data.changes || {};
        
        // Build combined data structure
        const combinedRates = {};
        
        // USD
        if (rates.USD_JMD) {
          const change = changes.JMD_USD || { change_percent: 0, direction: "neutral" };
          combinedRates.USD = { 
            rate: rates.USD_JMD, 
            change: change.change_percent || 0,
            direction: change.direction || "neutral"
          };
        } else {
          combinedRates.USD = { rate: 155.50, change: 0.5, direction: "up" };
        }
        
        // GBP
        if (rates.GBP_JMD) {
          const change = changes.JMD_GBP || { change_percent: 0, direction: "neutral" };
          combinedRates.GBP = { 
            rate: rates.GBP_JMD, 
            change: change.change_percent || 0,
            direction: change.direction || "neutral"
          };
        } else {
          combinedRates.GBP = { rate: 197.20, change: -0.3, direction: "down" };
        }
        
        // CAD
        if (rates.CAD_JMD) {
          const change = changes.JMD_CAD || { change_percent: 0, direction: "neutral" };
          combinedRates.CAD = { 
            rate: rates.CAD_JMD, 
            change: change.change_percent || 0,
            direction: change.direction || "neutral"
          };
        } else {
          combinedRates.CAD = { rate: 114.30, change: 0.1, direction: "up" };
        }
        
        // EUR
        combinedRates.EUR = { rate: 168.40, change: 0.2, direction: "up" };
        
        return { rates: combinedRates };
      } catch {
        return {
          rates: {
            USD: { rate: 155.50, change: 0.5, direction: "up" },
            GBP: { rate: 197.20, change: -0.3, direction: "down" },
            CAD: { rate: 114.30, change: 0.1, direction: "up" },
            EUR: { rate: 168.40, change: 0.2, direction: "up" }
          }
        };
      }
    },
    staleTime: 60000,
    retry: false,
  });

  // Load rotation state from storage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ROTATION_STORAGE_KEY);
      if (stored) {
        const { index, timestamp } = JSON.parse(stored);
        setRotationIndex(index || 0);
        setLastRotationTime(timestamp || 0);
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Rotate on app open / returning to home (with cooldown)
  useEffect(() => {
    const now = Date.now();
    if (now - lastRotationTime > ROTATION_COOLDOWN_MS) {
      const newIndex = (rotationIndex + 1) % 2;
      setRotationIndex(newIndex);
      setLastRotationTime(now);
      
      try {
        localStorage.setItem(ROTATION_STORAGE_KEY, JSON.stringify({
          index: newIndex,
          timestamp: now,
        }));
      } catch {
        // Ignore storage errors
      }
    }
  }, []);

  // Handle currency selection
  const handleSelectCurrency = (currency) => {
    setWatchedCurrency(currency);
    localStorage.setItem(CURRENCY_WATCH_KEY, currency);
    setShowCurrencyWatcher(false);
    toast.success(`Now watching ${currency} rates`);
  };

  // Get watched currency rate data
  const watchedRate = fxData?.rates?.[watchedCurrency] || { rate: 155.50, change: 0.5, direction: "up" };
  const isRateFavorable = watchedRate.direction === "down" || watchedRate.change < 0;

  // Build card data context
  const cardData = useMemo(() => ({
    bills: billsData?.bills || [],
    activePromo: promoData?.activePromo,
    fxRates: fxData?.rates || {},
    watchedCurrency,
    watchedRate,
    isRateFavorable,
  }), [billsData, promoData, fxData, watchedCurrency, watchedRate, isRateFavorable]);

  // Determine which cards to show
  const visibleCards = useMemo(() => {
    // Slot A: Always Earn Cashback
    const slotA = { id: "earnCashback", slot: "A" };

    // Slot B: Bill Due Soon if eligible, else rotate between Pure Vybz and FX Rate
    let slotB;
    
    if (cardData.bills?.length > 0) {
      slotB = { id: "billDueSoon", slot: "B" };
    } else {
      const rotatingCards = [{ id: "pureVybz" }, { id: "fxRateUpdate" }];
      slotB = rotatingCards[rotationIndex % rotatingCards.length];
    }

    return [slotA, slotB];
  }, [cardData.bills, rotationIndex]);

  // Render Earn Cashback card
  const EarnCashbackCard = () => (
    <button
      onClick={() => navigate("/lifestyle")}
      data-testid="smart-card-earnCashback"
      className="flex-1 glass-light rounded-2xl p-4 flex flex-col items-start gap-2 
        transition-all hover:bg-white/10 active:scale-[0.98] text-left min-h-[100px]
        bg-gradient-to-br from-emerald-500/20 to-green-500/10"
    >
      <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center">
        <Percent className="w-4.5 h-4.5 text-emerald-400" />
      </div>
      <p className="text-sm font-semibold text-white leading-tight">Earn Cashback</p>
      <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
        {cardData.activePromo 
          ? `Earn up to ${cardData.activePromo.percent}% on ${cardData.activePromo.type}`
          : "Unlock rewards when you send money"}
      </p>
      <div className="flex items-center gap-1 mt-auto pt-1">
        <span className="text-[11px] font-medium text-emerald-400">
          {cardData.activePromo ? "Earn Now" : "View Rewards"}
        </span>
        <ArrowRight className="w-3 h-3 text-emerald-400" />
      </div>
    </button>
  );

  // Render Bill Due Soon card
  const BillDueSoonCard = () => {
    const bills = cardData.bills || [];
    let subtext = "";
    if (bills.length === 1) {
      const bill = bills[0];
      const daysLeft = bill.daysUntilDue;
      if (daysLeft === 0) subtext = `${bill.name} bill due today`;
      else if (daysLeft === 1) subtext = `${bill.name} bill due tomorrow`;
      else subtext = `${bill.name} bill due in ${daysLeft} days`;
    } else if (bills.length > 1) {
      subtext = `${bills.length} bills due this week`;
    }

    return (
      <button
        onClick={() => navigate("/bills")}
        data-testid="smart-card-billDueSoon"
        className="flex-1 glass-light rounded-2xl p-4 flex flex-col items-start gap-2 
          transition-all hover:bg-white/10 active:scale-[0.98] text-left min-h-[100px]
          bg-gradient-to-br from-orange-500/20 to-amber-500/10"
      >
        <div className="w-9 h-9 rounded-xl bg-orange-500/20 flex items-center justify-center">
          <Receipt className="w-4.5 h-4.5 text-orange-400" />
        </div>
        <p className="text-sm font-semibold text-white leading-tight">Bill Due Soon</p>
        <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">{subtext}</p>
        <div className="flex items-center gap-1 mt-auto pt-1">
          <span className="text-[11px] font-medium text-orange-400">Pay Now</span>
          <ArrowRight className="w-3 h-3 text-orange-400" />
        </div>
      </button>
    );
  };

  // Render Pure Vybz card
  const PureVybzCard = () => (
    <button
      onClick={() => navigate("/events")}
      data-testid="smart-card-pureVybz"
      className="flex-1 glass-light rounded-2xl p-4 flex flex-col items-start gap-2 
        transition-all hover:bg-white/10 active:scale-[0.98] text-left min-h-[100px]
        bg-gradient-to-br from-purple-500/20 to-pink-500/10"
    >
      <div className="w-9 h-9 rounded-xl bg-purple-500/20 flex items-center justify-center">
        <Ticket className="w-4.5 h-4.5 text-purple-400" />
      </div>
      <p className="text-sm font-semibold text-white leading-tight">Pure Vybz</p>
      <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
        Pay with Stelx • VIP Access
      </p>
      <div className="flex items-center gap-1 mt-auto pt-1">
        <span className="text-[11px] font-medium text-purple-400">View Events</span>
        <ArrowRight className="w-3 h-3 text-purple-400" />
      </div>
    </button>
  );

  // Render FX Rate Update card with indicator
  const FXRateUpdateCard = () => (
    <button
      onClick={() => setShowCurrencyWatcher(true)}
      data-testid="smart-card-fxRateUpdate"
      className="flex-1 glass-light rounded-2xl p-4 flex flex-col items-start gap-2 
        transition-all hover:bg-white/10 active:scale-[0.98] text-left min-h-[100px]
        bg-gradient-to-br from-cyan-500/20 to-blue-500/10"
    >
      <div className="flex items-center justify-between w-full">
        <div className="w-9 h-9 rounded-xl bg-cyan-500/20 flex items-center justify-center">
          <TrendingUp className="w-4.5 h-4.5 text-cyan-400" />
        </div>
        {/* Rate Change Indicator */}
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${
          isRateFavorable 
            ? "bg-emerald-500/20 text-emerald-400" 
            : "bg-rose-500/20 text-rose-400"
        }`}>
          {isRateFavorable ? (
            <ArrowDownRight className="w-3 h-3" />
          ) : (
            <ArrowUpRight className="w-3 h-3" />
          )}
          <span>{Math.abs(watchedRate.change || 0.5).toFixed(1)}%</span>
        </div>
      </div>
      <p className="text-sm font-semibold text-white leading-tight">FX Rate Update</p>
      <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
        {isRateFavorable 
          ? `${watchedCurrency}→JMD rates favorable` 
          : `${watchedCurrency}→JMD J$${watchedRate.rate?.toFixed(2) || "155.50"}`}
      </p>
      <div className="flex items-center gap-1 mt-auto pt-1">
        <Eye className="w-3 h-3 text-cyan-400" />
        <span className="text-[11px] font-medium text-cyan-400">Watch Rates</span>
      </div>
    </button>
  );

  // Render card by ID
  const renderCard = (cardInfo) => {
    switch (cardInfo.id) {
      case "earnCashback":
        return <EarnCashbackCard key="earnCashback" />;
      case "billDueSoon":
        return <BillDueSoonCard key="billDueSoon" />;
      case "pureVybz":
        return <PureVybzCard key="pureVybz" />;
      case "fxRateUpdate":
        return <FXRateUpdateCard key="fxRateUpdate" />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex gap-3 mb-6" data-testid="smart-cards-row">
        {visibleCards.map((card) => renderCard(card))}
      </div>

      {/* Currency Watcher Dialog */}
      <Dialog open={showCurrencyWatcher} onOpenChange={setShowCurrencyWatcher}>
        <DialogContent className="glass-heavy border-white/[0.08] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Watch Currency Rates</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Select a currency to track. You'll see rate changes at a glance.
            </p>
            
            <div className="space-y-2">
              {[
                { code: "USD", name: "US Dollar", flag: "🇺🇸" },
                { code: "GBP", name: "British Pound", flag: "🇬🇧" },
                { code: "CAD", name: "Canadian Dollar", flag: "🇨🇦" },
                { code: "EUR", name: "Euro", flag: "🇪🇺" }
              ].map((currency) => {
                const rateData = fxData?.rates?.[currency.code] || { rate: 0, change: 0, direction: "neutral" };
                const isSelected = watchedCurrency === currency.code;
                const isFavorable = rateData.direction === "down" || rateData.change < 0;
                
                return (
                  <button
                    key={currency.code}
                    onClick={() => handleSelectCurrency(currency.code)}
                    data-testid={`currency-option-${currency.code}`}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                      isSelected 
                        ? "bg-primary/20 border border-primary/50" 
                        : "bg-white/5 hover:bg-white/10 border border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{currency.flag}</span>
                      <div className="text-left">
                        <p className="font-medium text-white">{currency.code}</p>
                        <p className="text-xs text-muted-foreground">{currency.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-mono text-sm text-white">
                          J${rateData.rate?.toFixed(2) || "-"}
                        </p>
                        <div className={`flex items-center justify-end gap-1 text-xs ${
                          isFavorable ? "text-emerald-400" : "text-rose-400"
                        }`}>
                          {isFavorable ? (
                            <TrendingDown className="w-3 h-3" />
                          ) : (
                            <TrendingUp className="w-3 h-3" />
                          )}
                          <span>{Math.abs(rateData.change || 0).toFixed(1)}%</span>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="pt-2 border-t border-white/10">
              <p className="text-xs text-muted-foreground text-center">
                <span className="text-emerald-400">↓ Green</span> = Rate favorable for sending to JMD
              </p>
            </div>

            <Button
              onClick={() => {
                setShowCurrencyWatcher(false);
                navigate("/exchange");
              }}
              className="w-full btn-primary h-11"
            >
              View All Rates & Exchange
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SmartCards;
